import { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Link } from 'react-router-dom';
import api, { agencyService } from '../services/api';
import { AppNav } from '../components/AppNav';
import { NoAgencyBanner } from '../components/NoAgencyBanner';

type LeadStatus = 'new' | 'contacted' | 'active' | 'closed';
type LeadSource = 'ad' | 'search' | 'direct';

interface Lead {
  id: string;
  agency_id: string;
  agent_id: string | null;
  client_id?: string | null;
  source: LeadSource;
  status: LeadStatus;
  name: string | null;
  email: string | null;
  phone: string | null;
  created_at: string | null;
}

const COLUMNS: { key: LeadStatus; label: string; dot: string; col: string; accent: string }[] = [
  { key: 'new',       label: 'Nuevos',      dot: 'bg-blue-400',    col: 'bg-blue-50/60',    accent: 'bg-blue-400' },
  { key: 'contacted', label: 'Contactados', dot: 'bg-amber-400',   col: 'bg-amber-50/60',   accent: 'bg-amber-400' },
  { key: 'active',    label: 'Activos',     dot: 'bg-emerald-400', col: 'bg-emerald-50/60', accent: 'bg-emerald-400' },
  { key: 'closed',    label: 'Cerrados',    dot: 'bg-stone-400',   col: 'bg-stone-50/60',   accent: 'bg-stone-300' },
];

interface MemberOpt {
  id: string;
  role: string;
  email?: string | null;
}

const STATUS_ORDER: LeadStatus[] = ['new', 'contacted', 'active', 'closed'];

const SOURCE_LABEL: Record<LeadSource, string> = {
  ad: 'Anuncio',
  search: 'Búsqueda',
  direct: 'Directo',
};

function LeadCard({
  lead,
  index,
  onStatusChange,
  onLeadChange,
  members,
  accent,
}: {
  lead: Lead;
  index: number;
  onStatusChange: (id: string, status: LeadStatus) => void;
  onLeadChange: (lead: Lead) => void;
  members: MemberOpt[];
  accent: string;
}) {
  const [moving, setMoving] = useState(false);
  // QA 2026-10-23 (F-007): asignar y convertir existían solo en la API — el
  // flujo A (lead → agente → cliente) no se podía completar desde la UI.
  const [converting, setConverting] = useState(false);
  const [password, setPassword] = useState('');
  const [cardError, setCardError] = useState('');
  const [saving, setSaving] = useState(false);

  const assign = async (agentId: string) => {
    if (!agentId) return;
    setSaving(true);
    setCardError('');
    try {
      const r = await api.put(`/leads/${lead.id}/assign`, { agent_id: agentId });
      onLeadChange({ ...lead, ...r.data });
    } catch {
      setCardError('No se pudo asignar.');
    } finally {
      setSaving(false);
    }
  };

  const convert = async () => {
    setSaving(true);
    setCardError('');
    try {
      const r = await api.post(`/leads/${lead.id}/convert`, { password });
      onLeadChange({ ...lead, client_id: r.data.id, status: 'active' });
      setConverting(false);
      setPassword('');
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const status = res?.status;
      const alreadyConverted = typeof res?.data?.detail === 'string' && res.data.detail.includes('already converted');
      setCardError(
        alreadyConverted
          ? 'Este lead ya se había convertido; recarga la página.'
          : status === 409
          ? 'Ya existe un cliente con ese correo.'
          : status === 422
            ? 'El lead necesita correo y nombre para convertirse.'
            : 'No se pudo convertir.',
      );
    } finally {
      setSaving(false);
    }
  };

  const advance = async () => {
    const next = STATUS_ORDER[STATUS_ORDER.indexOf(lead.status) + 1];
    if (!next) return;
    setMoving(true);
    const prev = lead.status;
    onStatusChange(lead.id, next);
    try {
      await api.put(`/leads/${lead.id}/status`, { status: next });
    } catch {
      onStatusChange(lead.id, prev);
    } finally {
      setMoving(false);
    }
  };

  return (
    <Draggable draggableId={lead.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`flex bg-white border border-border/80 rounded-xl overflow-hidden shadow-sm transition-shadow ${
            snapshot.isDragging ? 'shadow-lg rotate-[0.5deg]' : 'hover:shadow-md'
          }`}
        >
          <div className={`w-[3px] flex-shrink-0 ${accent}`} />
          <div className="p-3.5 flex-1 min-w-0 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {lead.name ? (
                  <p className="font-medium text-sm truncate">{lead.name}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Sin nombre</p>
                )}
                {lead.email && <p className="text-xs text-muted-foreground truncate">{lead.email}</p>}
              </div>
              <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md shrink-0 font-medium">
                {SOURCE_LABEL[lead.source]}
              </span>
            </div>
            {lead.phone && <p className="text-xs text-muted-foreground">{lead.phone}</p>}
            {members.length > 0 && (
              <select
                aria-label="Asignar agente"
                value={lead.agent_id ?? ''}
                disabled={saving}
                onChange={(e) => assign(e.target.value)}
                className="w-full text-xs border border-border rounded-md px-2 py-1 bg-white"
              >
                <option value="" disabled>Asignar a…</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.email ?? m.id.slice(0, 8)} {m.role === 'admin' ? '(admin)' : ''}
                  </option>
                ))}
              </select>
            )}
            {lead.client_id ? (
              <Link to={`/agent/clients/${lead.client_id}`} className="block text-[11px] text-emerald-700 font-medium">
                Cliente creado · ver ficha →
              </Link>
            ) : lead.email && converting ? (
              <div className="space-y-1.5">
                <input
                  type="password"
                  aria-label="Contraseña del portal del cliente"
                  placeholder="Contraseña del portal (mín. 8)"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full text-xs border border-border rounded-md px-2 py-1"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={convert}
                    disabled={saving || password.length < 8}
                    className="text-[11px] bg-primary text-primary-foreground px-2 py-1 rounded-md disabled:opacity-50"
                  >
                    Crear cliente
                  </button>
                  <button type="button" onClick={() => setConverting(false)} className="text-[11px] text-muted-foreground">
                    Cancelar
                  </button>
                </div>
              </div>
            ) : lead.email ? (
              <button
                type="button"
                onClick={() => setConverting(true)}
                className="block text-[11px] text-primary font-medium"
              >
                Convertir en cliente
              </button>
            ) : null}
            {cardError && <p className="text-[11px] text-destructive">{cardError}</p>}
            {lead.status !== 'closed' && (
              <button
                onClick={advance}
                disabled={moving}
                className="text-[11px] text-primary hover:text-primary/80 font-medium disabled:opacity-50 transition-colors"
              >
                {moving ? 'Moviendo...' : 'Avanzar →'}
              </button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

function LeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [noAgency, setNoAgency] = useState(false);
  const [members, setMembers] = useState<MemberOpt[]>([]);

  useEffect(() => {
    document.title = 'Leads — Takarah';
    api.get('/leads')
      .then((r) => setLeads(r.data))
      .catch((err) => {
        if (err?.response?.status === 403) {
          setNoAgency(true);
        } else {
          setError('No se pudieron cargar los leads');
        }
      })
      .finally(() => setLoading(false));
    // Miembros para el selector de asignación (best-effort: sin ellos, la
    // tarjeta simplemente no muestra el selector)
    agencyService
      .getMe()
      .then((me: { agency?: { id: string } }) => (me?.agency ? agencyService.getMembers(me.agency.id) : null))
      .then((r: { members?: MemberOpt[] } | null) => setMembers(r?.members ?? []))
      .catch(() => setMembers([]));
  }, []);

  const handleLeadChange = (updated: Lead) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  };

  const handleStatusChange = (id: string, status: LeadStatus) => {
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status } : l));
  };

  const onDragEnd = async (result: DropResult) => {
    const { draggableId, destination } = result;
    if (!destination) return;
    const newStatus = destination.droppableId as LeadStatus;
    const lead = leads.find((l) => l.id === draggableId);
    if (!lead || lead.status === newStatus) return;

    // Optimistic update
    setLeads((prev) => prev.map((l) => l.id === draggableId ? { ...l, status: newStatus } : l));
    try {
      await api.put(`/leads/${draggableId}/status`, { status: newStatus });
    } catch {
      // Revert on failure
      setLeads((prev) => prev.map((l) => l.id === draggableId ? { ...l, status: lead.status } : l));
    }
  };

  const byStatus = (s: LeadStatus) => leads.filter((l) => l.status === s);

  if (noAgency) return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <NoAgencyBanner />
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {error && <p className="text-sm text-destructive mb-4">{error}</p>}

        {loading ? (
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
            {COLUMNS.map((col) => (
              <div key={col.key} className="space-y-3">
                <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                {[1, 2].map((i) => (
                  <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
              {COLUMNS.map((col) => {
                const colLeads = byStatus(col.key);
                return (
                  <div key={col.key} className="flex flex-col gap-3">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${col.col}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />
                      <p className="text-xs font-semibold tracking-wide text-foreground/70 truncate">
                        {col.label}
                      </p>
                      <span className="text-[11px] font-medium text-muted-foreground ml-auto tabular-nums">
                        {colLeads.length}
                      </span>
                    </div>
                    <Droppable droppableId={col.key}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`space-y-2 min-h-[120px] rounded-xl transition-colors ${
                            snapshot.isDraggingOver ? 'bg-muted/40' : ''
                          }`}
                        >
                          {colLeads.length === 0 && !snapshot.isDraggingOver ? (
                            <div className="border border-dashed border-border/60 rounded-xl p-5 text-center">
                              <p className="text-xs text-muted-foreground/60">Sin leads</p>
                            </div>
                          ) : (
                            colLeads.map((lead, index) => (
                              <LeadCard
                                key={lead.id}
                                lead={lead}
                                index={index}
                                accent={col.accent}
                                onStatusChange={handleStatusChange}
                                onLeadChange={handleLeadChange}
                                members={members}
                              />
                            ))
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        )}
      </main>
    </div>
  );
}

export default LeadsPage;
